import { z } from 'zod';
import type { QueryClient } from '@tanstack/react-query';
import { brandsQuery } from '@/api/queries';
import { upsertPurchaseOrder } from '@/api/endpoints';
import { getSession } from '@/api/session';
import type { PurchaseOrderUpsertRequest, PurchaseOrderItemInput } from '@/api/types';
import type { ToolConfig } from './utils/create-tool';
import { handlePoError } from './utils/po-error-handler';

const inputSchema = z.object({
  brand_name: z.string(),
  title: z.string().optional(),
  expected_arrival_date: z.string().optional(),
  items: z.array(
    z.object({
      product_name: z.string(),
      requested_quantity: z.number(),
      unit_price_in_cents: z.number().optional().default(0),
      unit_of_measure: z
        .enum(['items', 'grams', 'kilograms', 'pounds', 'milligrams', 'ounces', 'milliliters'])
        .optional()
        .default('items'),
    }),
  ),
  note: z.string().optional(),
});

async function execute(params: z.infer<typeof inputSchema>, queryClient: QueryClient) {
  try {
    const email = getSession()?.Email;
    if (!email) {
      return {
        error: 'No email on file. Please provide your email to create a purchase order.',
      };
    }

    // Resolve brand name → brandCatalogBrandId
    const brands = await queryClient.fetchQuery(brandsQuery());
    const needle = params.brand_name.toLowerCase();

    const exactMatch = brands.find((b) => b.brandName.toLowerCase() === needle);
    const fuzzyMatch = exactMatch ?? brands.find((b) => b.brandName.toLowerCase().includes(needle));

    if (!fuzzyMatch) {
      return {
        error: `Brand "${params.brand_name}" not found. Use get_brands to see available brands.`,
      };
    }

    if (!fuzzyMatch.brandCatalogBrandId) {
      return {
        error: `Brand "${fuzzyMatch.brandName}" is not linked to the ordering catalog. It cannot be used for purchase orders.`,
      };
    }

    // Build items
    const purchase_order_items: PurchaseOrderItemInput[] = params.items.map((item) => ({
      catalog_source: 'retailer' as const,
      retailer_product_name: item.product_name,
      requested_quantity: item.requested_quantity,
      unit_price_in_cents: item.unit_price_in_cents,
      unit_of_measure: item.unit_of_measure,
    }));

    // Build upsert body — server fills in source_dpos_* and changed_by_party
    const body: PurchaseOrderUpsertRequest = {
      brand_id: fuzzyMatch.brandCatalogBrandId,
      title: params.title,
      expected_arrival_date: params.expected_arrival_date,
      changed_by_party: 'retailer',
      created_by_email: [email],
      purchase_order_items,
    };

    if (params.note) {
      body.notes = [
        {
          author_party: 'retailer',
          author_email: '',
          content: params.note,
        },
      ];
    }

    const result = await upsertPurchaseOrder(body);
    const po = result.data.attributes;

    // Invalidate list cache
    await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });

    return {
      success: true,
      purchase_order_id: po.id,
      po_number: po.purchaseOrderNumber,
      status: po.status,
      brand_name: po.brandName,
      item_count: po.items,
      message: `Draft ${po.purchaseOrderNumber} created with ${po.items} item(s) for ${po.brandName}. Review it in the Purchase Orders tab, or ask me to submit it.`,
    };
  } catch (error) {
    return handlePoError(error);
  }
}

export const draftPurchaseOrderTool: ToolConfig<typeof inputSchema> = {
  name: 'draft_purchase_order',
  description:
    'Creates a draft purchase order for a brand with line items. Does NOT submit — the order stays as a draft for review. The operator must explicitly confirm before you call this. Always show a summary of items and ask for confirmation first.',
  inputSchema,
  execute,
};
